<script setup lang="ts">
import {Dialog, DialogDescription, DialogHeader, DialogScrollContent, DialogTitle} from "@/core/components/ui/dialog";
import {CalendarHeart} from "@lucide/vue";
import {Appointment} from "@/core/interfaces/Appointment";
import {computed, markRaw, ref, watch} from "vue";
import AppointmentsCreationStep
    from "@/appointment/components/consumer-appointment-timeline/start-journey/AppointmentsCreationStep.vue";
import Customer from "@/customer/interfaces/Customer";
import AppointmentNotificationStep
    from "@/appointment/components/consumer-appointment-timeline/start-journey/AppointmentNotificationStep.vue";
import {motion, AnimatePresence} from "motion-v";
import {AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import {useToastStore} from "@/stores/toast.store";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {appointmentApi} from "@/container";
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";

enum STEP {
    APPOINTMENT_CREATION = 1,
    NOTIFICATION = 2
}

const props = defineProps<{
    open: boolean;
    customer: Customer;
    supportProgramId?: number;
}>();

const emits = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'batch-created', appointments: Appointment[]): void
    (e: 'open-certification-dialog'): void;
}>();

watch(() => props.open, (newVal) => {
    if (!newVal) {
        currentStep.value = STEP.APPOINTMENT_CREATION;
    }
});

const toastStore = useToastStore();
const {environment} = storeToRefs(useSystemStore());

const loadingCreate = ref(false);
const currentStep = ref<STEP>(STEP.APPOINTMENT_CREATION);

const headerConfig = computed(() => {
    switch (currentStep.value) {
        case STEP.APPOINTMENT_CREATION:
            return {
                title: 'Planifiez les prochains rendez-vous du patient',
                description: 'Sélectionnez la date puis complétez les informations',
                icon: markRaw(CalendarHeart)
            };
        case STEP.NOTIFICATION:
            return {
                title: 'Rappels automatiques',
                description: null
            };
        default:
            return {
                title: '',
                description: ''
            };
    }
});

/**
 * Create multiple appointments for the customer.
 */
const createAppointments = async (items: AppointmentCreateRequest[]) => {
    const organizationId = environment.value?.organizationId;

    if (!organizationId) {
        throw new Error('Organization ID is not defined in the environment.');
    }

    loadingCreate.value = true;
    return appointmentApi().createAppointmentBatch(organizationId, items)
        .then((appointments) => {
            toastStore.showSuccess({content: 'Rendez-vous créés avec succès !'});

            emits('batch-created', appointments);

            if (props.open) {
                currentStep.value = STEP.NOTIFICATION;
            }
        })
        .catch((error) => {
            if (!isAuthorizationError(error)) {
                toastStore.showError({content: apiErrorMessage(error)});
            }
        })
        .finally(() => {
            loadingCreate.value = false;
        });
};
</script>

<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogScrollContent
      class="sm:max-w-[650px] relative flex flex-col overflow-hidden p-0 overflow-y-auto max-h-[90vh]"
    >
      <DialogHeader class="px-6 pt-6">
        <DialogTitle class="flex items-center gap-2">
          <component :is="headerConfig.icon" v-if="headerConfig.icon" class="w-5 h-5 text-primary" />
          {{ headerConfig.title }}
        </DialogTitle>
        <DialogDescription v-if="headerConfig.description" class="text-start">
          {{ headerConfig.description }}
        </DialogDescription>
      </DialogHeader>
      <div class="bg-gray-50">
        <AnimatePresence mode="wait">
          <motion.div
            v-if="currentStep === STEP.APPOINTMENT_CREATION"
            key="appointment-creation"
            :initial="{ x: 0 }"
            :animate="{ x: 0 }"
            :exit="{ x: -400 }"
            :transition="{ duration: 0.2, ease: 'linear' }"
            class="w-full"
          >
            <AppointmentsCreationStep
              :customer-id="customer.id"
              :support-program-id="supportProgramId"
              :loading="loadingCreate"
              @submit="createAppointments"
            />
          </motion.div>

          <motion.div
            v-else-if="currentStep === STEP.NOTIFICATION"
            key="notification"
            :initial="{ x: 400 }"
            :animate="{ x: 0 }"
            :exit="{ x: -400 }"
            :transition="{ duration: 0.2, ease: 'linear' }"
            class="w-full"
          >
            <AppointmentNotificationStep
              :customer="customer"
              @close="$emit('update:open', false)"
              @open-certification-dialog="$emit('open-certification-dialog')"
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
